import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RootState, AppDispatch } from "../../store";
import { fetchList, resetState } from "../../store/slices/listSlice";
import EmptyListingScreen from "../emptyScreens/EmptyListingScreen";
import TableHeader from "../tables/TableHeader";
import TableTopBar from "../tables/TableTopBar";
import { TableDetailsItem } from "../tables/tableFunctions";
import Paginate from "../tables/Paginate";
import useTranslation from "../CustomHooks/useTranslation";
import { breadcrumbsContent } from "../breadcrumbs/BreadcrumbsContent";
import GridTableTopBar from "./GridTableTopBar";
import GridTableHeader from "./GridTableHeader";
import { DynamicFilterConfig } from "../DynamicComponent/DynamicFilterPanel";
import dayjs from "dayjs";

interface MainTableProps {
  staticText: {
    emptyScreenText: {
      heading1: string;
      heading2: string;
    };
  };
  mainEndPoint: string;
  TableDetails: TableDetailsItem;
  handleActions: (id: string, key: string, text?: string) => void;
  params?: { [key: string]: string };
  searchPlaceholder?: string;
  filters: boolean;
  exporting: boolean;
  serviceNow?: boolean;
  displayTotalRecords?: boolean;
  noListing?: boolean;
  notfoundRoute?: { [key: string]: { [key: string]: string } };
  title?: string;
  placeholderClassName?: string;
  dynamicFilterConfig?: DynamicFilterConfig;
  renderTopBarExtras?: (args: {
    entireResponse: any;
    searchParams: URLSearchParams;
    setSearchParams: any;
    loading: boolean;
  }) => React.ReactNode;
  renderSummary?: (args: { entireResponse: any, loading: boolean }) => React.ReactNode;
  heightClassName?: string;
  hasActivateDeactivateMDPrivilege?: boolean;
  hasViewMDPrivilege?: boolean;
  hasEditMDPrivilege?: boolean;
  addMorePadding?: boolean;
}

export default function GridTable({
  staticText,
  mainEndPoint,
  TableDetails,
  handleActions,
  noListing,
  params ={},
  searchPlaceholder,
  filters,
  serviceNow,
  exporting,
  displayTotalRecords = false,
  notfoundRoute = {},
  title,
  placeholderClassName,
  dynamicFilterConfig,
  renderTopBarExtras,
  renderSummary,
  heightClassName,
  hasActivateDeactivateMDPrivilege,
  hasViewMDPrivilege,
  hasEditMDPrivilege,
  addMorePadding
}: MainTableProps) {
  const dispatch = useDispatch<AppDispatch>();
  const listState = useSelector((state: RootState) => state.list);
  const { loading, error, data, recordCount, entireResponse } = listState;
  const {translate} = useTranslation(breadcrumbsContent)
  const navigate = useNavigate()
  const filersWithEmpty = {
    heading1: translate("No results found"),
    heading2: translate("Try adjusting your filter's or search criteria"),
    img: "search_empty_screen_img",
  };

  const [searchParams, setSearchParams] = useSearchParams(params);

  const getList = () => {
    const params = Object.fromEntries(searchParams.entries());
    dispatch(
      fetchList(`${mainEndPoint}?${new URLSearchParams(params).toString()}`)
    );
  };
  useEffect(() => {
    getList();
  }, [searchParams]);
  const handleSearch = (searchTerm: string) => {
    handleFilter({ search: searchTerm });
  };
  const handleFilter = (filters: { [key: string]: any } | null) => {
    setSearchParams((prev) => {
      const updatedParams = new URLSearchParams(prev);
      updatedParams.set("page_number", "1");
      // If filters is null, clear all filter keys
      if (filters === null && dynamicFilterConfig) {
        Object.values(dynamicFilterConfig).forEach((conf) => {
          if (conf.type === "date" && typeof conf.searchKey === "object") {
            updatedParams.delete(conf.searchKey.start);
            updatedParams.delete(conf.searchKey.end);
          } else if (typeof conf.searchKey === "string") {
            updatedParams.delete(conf.searchKey);
          }
        });
        return updatedParams;
      }
      // ...existing logic for non-null filters...
      if (filters) {
        Object.keys(filters).forEach((key) => {
          let value = filters[key];
          // Handle date range objects
          if (
            value &&
            typeof value === "object" &&
            ("start" in value || "end" in value)
          ) {
            const { start, end } = value as { start?: dayjs.Dayjs | null; end?: dayjs.Dayjs | null };
            if (start && typeof start.format === "function") {
              updatedParams.set(`${key}_start`, start.format("YYYY-MM-DD"));
            } else {
              updatedParams.delete(`${key}_start`);
            }
            if (end && typeof end.format === "function") {
              updatedParams.set(`${key}_end`, end.format("YYYY-MM-DD"));
            } else {
              updatedParams.delete(`${key}_end`);
            }
            // Don't set the main key for the range object
            updatedParams.delete(key);
          }
          // Handle single dayjs object
          else if (value && typeof value === "object" && typeof (value as any).format === "function") {
            value = (value as dayjs.Dayjs).format("YYYY-MM-DD");
            updatedParams.set(key, value.toString());
          }
          // Handle primitives
          else if (value !== null && value !== "") {
            updatedParams.set(key, value.toString());
          } else {
            updatedParams.delete(key);
          }
        });
      }
      return updatedParams;
    });
  };
  const handleSort = (key: string) => {
    setSearchParams((prev) => {
      const updatedParams = new URLSearchParams(prev);
      const currentSort = updatedParams.get("sort");
      let newSort;

      if (currentSort) {
        const sortArray = currentSort.split(",");
        const existingSort = sortArray.find((s) => s.startsWith(key));

        if (existingSort) {
          // Toggle between asc and desc
          const [field, order] = existingSort.split("+");
          const newOrder = order === "asc" ? "desc" : "asc";
          newSort = [`${field}+${newOrder}`, ...sortArray.filter((s) => !s.startsWith(key))].join(",");
        } else {
          // Add new sort key at the start
          newSort = [`${key}+asc`, ...sortArray].join(",");
        }
      } else {
        // Set initial sort
        newSort = `${key}+asc`;
      }
      updatedParams.set("page_number", "1");
      updatedParams.set("sort", newSort);
      return updatedParams;
    });
  };

  const navigator = () => {
    dispatch(resetState());
    navigate('/not-found', notfoundRoute);
  };

  useEffect(() => {
    if (error !== null) {
      navigator();
      dispatch(resetState());
    }
  }, [error]);

  const hasActiveFilters = Array.from(searchParams.entries())
  .filter(([key]) => !params[key]) // Exclude keys present in param
  .length > 0;
  const exortSearchParams = new URLSearchParams(
    Array.from(searchParams.entries()).filter(([key]) => key !== "page_number")
  );
  return (
    <div
      className={`mb-4 ${addMorePadding ? 'mr-[34px] ml-3' : 'mx-3'} mt-3 bg-white h-maintableheight rounded-lg`}
    >
      {!noListing && (
        <GridTableTopBar
          onSearch={handleSearch}
          onFilter={handleFilter}
          loading={loading}
          searchValue={searchParams.get("search") || ""}
          searchParams={searchParams}
          searchPlaceholder={searchPlaceholder}
          filters={filters}
          serviceNow={serviceNow}
          exporting={exporting}
          endpoint={`${mainEndPoint + '/'}export?${exortSearchParams.toString()}`}
          disableExport={data && data.length === 0}
          disableSearchAndFilter={
            recordCount === 0 &&
            hasActiveFilters === false &&
            searchParams.get("search") === null
          }
          placeholderClassName={placeholderClassName}
          dynamicFilterConfig={dynamicFilterConfig}
          recordCount={recordCount}
        />
      )}
      {renderTopBarExtras &&
        renderTopBarExtras({ entireResponse, searchParams, setSearchParams, loading })}
      {loading ? (
        <div className="p-4">
          <GridTableHeader
            listData={data}
            TableDetails={TableDetails}
            handleActions={handleActions}
            loading={loading}
            handleSort={handleSort}
            heightClassName={heightClassName}
          />
          {renderSummary && renderSummary({ entireResponse, loading })}
          <Paginate
            totalPages={Math.ceil((recordCount || 0) / 10)}
            currentPage={Number(searchParams.get("page_number")) || 1}
            onPageChange={(page) =>
              setSearchParams({
                ...Object.fromEntries(searchParams),
                page_number: String(page),
              })
            }
            loading={loading}
          />
        </div>
      ) : error ? (
        <div className="text-center py-8 text-red-500">error</div>
      ) : !noListing && data && data.length > 0 ? (
        <div className="p-4">
          <GridTableHeader
            listData={data}
            TableDetails={TableDetails}
            handleActions={handleActions}
            loading={loading}
            handleSort={handleSort}
            searchParams={searchParams}
            getList={getList}
            heightClassName={heightClassName}
            hasActivateDeactivateMDPrivilege={hasActivateDeactivateMDPrivilege}
            hasViewMDPrivilege={hasViewMDPrivilege}
            hasEditMDPrivilege={hasEditMDPrivilege}
          />
          {(renderSummary) && renderSummary({ entireResponse, loading })}
          {recordCount && Number(recordCount) > 10 && (
            <Paginate
              totalPages={Math.ceil((recordCount || 0) / 10)}
              currentPage={Number(searchParams.get("page_number")) || 1}
              onPageChange={(page) =>
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  page_number: String(page),
                })
              }
              loading={loading}
              displayTotalRecords={displayTotalRecords}
              title={title}
            />
          )}
        </div>
      ) : (
        // use this when the totalrecodes are 0 "maintableheight"
        <div className="flex items-center justify-center h-emptyscreenheight">
          <EmptyListingScreen
            emptyScreenText={
              hasActiveFilters ? filersWithEmpty : staticText.emptyScreenText
            }
          />
        </div>
      )}
    </div>
  );
}
